import { useApolloClient } from '@apollo/react-hooks'
import { getObjectLabels } from '@paintscout/util/builder'
import { removeAuth0Prefix } from '@paintscout/util/users'
import { sanitizeFiltersWithAcceptedDate } from '@paintscout/util/util'
import * as Sentry from '@sentry/react'
import { useDebouncedCallback, useIsFirstRender, useIsMobile, AlertDialog, SearchBar } from '@paintscout/ui'
import type { SearchBarProps } from '@paintscout/ui'
import { useClientOptions, useDialogs, useUserPermissions } from '@paintscout/ui-business'
import { gql } from 'apollo-boost'
import FileSaver from 'file-saver'
import type { Location } from 'history'
import type { ValueFilter } from 'json-to-lucene'
import { jsonToLucene } from 'json-to-lucene'
import isEqual from 'lodash/isEqual'
import isUndefined from 'lodash/isUndefined'
import omitBy from 'lodash/omitBy'
import pick from 'lodash/pick'
import moment from 'moment'
import type { QuoteStatus } from 'paintscout'
import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { getUrlQuery, setUrlQuery } from 'shared/util/routing'
import QuoteSearchFilterChips from './QuoteSearchFilterChips'
import QuoteSearchFilters from './QuoteSearchFilters'

export interface QuoteSearchBarProps extends Omit<SearchBarProps, 'onSearch' | 'showFilters' | 'setShowFilters'> {
  loading?: boolean
  invoice?: boolean
  status?: string
  children?: React.ReactNode
  filters?: ValueFilter[]
  onSearch?: (query: string, filters: ValueFilter[], isInitialSearch?: boolean) => any
}

export interface UrlQuery {
  sort?: string
  search?: string
  filters: ValueFilter[]
  page?: string
}

interface LegacyUrlQuery {
  search?: string
  quote_date?: number[]
  status?: string
  int_ext?: string
  archived?: string
  totals?: number[]
}

const FILTER_STATUS_VALUE = 'status.value'
const FILTER_TOTALS_AFTER_TAX = 'totals.after_tax'

function QuoteSearchBar({ invoice, loading, onSearch, status, ...props }: QuoteSearchBarProps) {
  const { permissionToExport } = useUserPermissions()
  const { openDialog, dismissDialog } = useDialogs()
  const isFirstRender = useIsFirstRender()
  const { options } = useClientOptions()
  const apolloClient = useApolloClient()
  const location = useLocation()
  const mobile = useIsMobile()

  const [showFilters, setShowFilters] = useState(false)
  const [exporting, setExporting] = useState(false)
  // the normal text of the search straight from the input
  const [queryText, setQueryText] = useState(getUrlQuery<UrlQuery>({ location }).search)
  // the committed/current filters for the results
  const [filters, setFilters] = useState(props.filters ?? getFiltersFromLocation(location))
  // filters state before the user commits them by hitting search
  const [uncommittedFilters, setUncommittedFilters] = useState(filters)
  const objectLabels = getObjectLabels({ options, invoice })
  const useAcceptedDate = !options?.options?.quotes?.dontCountAcceptedDate
  const dateFormat = options.options.dateFormat.momentValue
  const placeholder = `Search ${objectLabels.quote.plural.toLowerCase()} by client, ID, job address, or value...`

  // update filters when status tab changes
  useEffect(() => {
    if (!isFirstRender && !filters.find((filter) => filter.id === FILTER_STATUS_VALUE)) {
      handleSearch({
        filters: [
          ...filters,
          {
            id: FILTER_STATUS_VALUE,
            type: 'equal',
            value: status
          }
        ]
      })
    }
    // eslint-disable-next-line
  }, [status])

  // trigger search when props.filters changes
  useEffect(() => {
    if (!isEqual(filters, props.filters)) {
      handleSearch({ filters: props.filters })
    }
    // eslint-disable-next-line
  }, [props.filters])

  // trigger search on mount
  useEffect(() => {
    handleSearch({ filters, query: queryText }, true)
  }, [])

  const handleSearch = (args: { query?: string; filters?: ValueFilter[] } = {}, isInitialSearch?: boolean) => {
    const { query = queryText } = args

    // the filter values (`filters` variable from state is in scope, so we'll call it values)
    const values = [...(args.filters ?? uncommittedFilters)]

    // update filter states
    setFilters(values)
    setUncommittedFilters(values)

    // update the typed-in user text
    setQueryText(query)

    // remove filters with null or undefined values
    const sanitizedValues = values.filter((filter) => {
      // != operator rather than !== is intentional - it checks for null or undefined - update to catch empty arrays
      return (
        filter.value != null &&
        (!Array.isArray(filter.value) || (Array.isArray(filter.value) && filter.value.filter(Boolean).length))
      )
    })

    setUrlQuery({
      location,
      params: { search: query || null, filters: sanitizedValues },
      replaceParams: true
    })

    onSearch(query, sanitizedValues, isInitialSearch)
  }

  const debouncedSearch = useDebouncedCallback(handleSearch, 100)

  const handleFilterClear = () => {
    setUncommittedFilters([])
  }

  const handleExport = async () => {
    const dateFilter = uncommittedFilters.find((filter) => filter.id === 'quote_date')
    const fileName = dateFilter
      ? // "Quote 01/01/2000 - 02/01/2000"
        `${objectLabels.quote.plural} ${moment(dateFilter.value[0]).format(dateFormat)} - ${moment(
          dateFilter.value[1]
        ).format(dateFormat)}.csv`
      : // "Quote <today's date>"
        `${objectLabels.quote.plural} ${moment(Date.now()).format(dateFormat)}.csv`

    setExporting(true)
    try {
      // use uncommitted filters since search box will still be open
      const withTrashedFilter: ValueFilter[] = [
        ...uncommittedFilters,
        { type: 'equal', id: 'trashed', value: true, not: true }
      ]
      const luceneQuery = jsonToLucene({
        query: queryText,
        filters: useAcceptedDate ? sanitizeFiltersWithAcceptedDate(withTrashedFilter) : withTrashedFilter
      })

      const sanitizedQuery = luceneQuery ? luceneQuery : '*:*'
      const query = (
        invoice ? `is_invoice:true AND (${sanitizedQuery})` : `NOT is_invoice:true AND (${sanitizedQuery})`
      ).replace(/\s*AND\s*\(\)/g, '')

      const { data } = await apolloClient.query({
        query: gql`
          query ($query: String!, $sort: [String!]) {
            exportQuotes(query: $query, sort: $sort) {
              csv
              rows
            }
          }
        `,
        variables: {
          query,
          // Need 2 sort params for tiebreaker, keep sort from quoteList if present else same default
          sort: [getUrlQuery<UrlQuery>({ location })?.sort ?? '-quote_date', '-dates.updated']
        }
      })

      if (data.exportQuotes.rows) {
        FileSaver.saveAs(new Blob([data.exportQuotes.csv]), fileName)
      } else {
        openDialog(AlertDialog, {
          message: 'No rows were found to export for this search query.',
          onConfirm: () => {
            dismissDialog()
          }
        })
      }
    } catch (error) {
      Sentry.captureException(error)
    }
    setExporting(false)
  }

  function deleteFilter({ id, valueIndex }: { id: string; valueIndex?: number }) {
    if (valueIndex >= 0 && (id === FILTER_STATUS_VALUE || id === 'owner.id' || id === 'int_ext' || id === 'tags')) {
      handleSearch({
        filters: filters
          .map((filter) => {
            if (filter.id === id && filter?.value) {
              if (filter.type === 'in' || filter.id === 'tags') {
                return {
                  ...filter,
                  value: (filter.value as any).filter((value, index) => index !== valueIndex)
                }
              } else {
                return null
              }
            } else {
              return filter
            }
          })
          .filter(Boolean)
      })
    } else {
      // Remove the only filter of type
      handleSearch({
        filters: filters.filter((filter) => {
          return filter.id !== id
        })
      })
    }
  }

  return (
    <div>
      <SearchBar
        inputProps={{
          'aria-label': `Search ${objectLabels.quote.plural}`,
          // recommended aria for loading indicator (also used in e2e tests)
          'aria-busy': loading,
          'aria-live': loading ? 'polite' : undefined
        }}
        filterMenuCollapse={!mobile}
        defaultValue={queryText}
        showExport={permissionToExport}
        placeholder={placeholder}
        isExporting={exporting}
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        filterContent={
          <QuoteSearchFilters invoice={invoice} filters={uncommittedFilters} onChange={setUncommittedFilters} />
        }
        onClear={handleFilterClear}
        onExport={handleExport}
        onChange={(q) => {
          const cleanQuery = q.replace(/\\/g, '/')
          debouncedSearch({ query: cleanQuery })
        }}
        onSearch={(q) => handleSearch({ query: q })}
        {...props}
      />
      <QuoteSearchFilterChips
        show={(filters ?? []).filter((filter) => filter.id != 'is_invoice').length > 0 && !showFilters}
        invoice={invoice}
        filters={filters}
        onDelete={(filter, valueIndex) => deleteFilter({ id: filter.id, valueIndex })}
      />
    </div>
  )
}

export function getFiltersFromLocation(location: Location, skipLegacy: boolean = false): ValueFilter[] {
  const { ...values } = getUrlQuery<UrlQuery | LegacyUrlQuery>({ location })

  if ((values as UrlQuery).filters) {
    return (values as UrlQuery).filters
  }
  // legacy url query
  else if (Object.keys(values).length > 0 && !skipLegacy) {
    const invoice = location.pathname.includes('invoice')
    const isContactOrCompany =
      location.pathname.includes('/people/contacts') || location.pathname.includes('/people/companies')
    const values = pick(
      omitBy(getUrlQuery<UrlQuery>({ location }), isUndefined),
      'quote_date',
      FILTER_STATUS_VALUE,
      FILTER_TOTALS_AFTER_TAX,
      'int_ext',
      'archived',
      'owner.id'
    )

    return convertLegacyFilters(values, invoice, isContactOrCompany)
  }

  return []
}

export function getQueryFromLocation(location: Location) {
  const { search } = getUrlQuery<UrlQuery>({ location })
  const filters = getFiltersFromLocation(location)

  return jsonToLucene({ query: search, filters }) || '*:*'
}

function convertLegacyFilters(
  filters: {
    quote_date: [number, number]
    archived: string
    'totals.after_tax': [number, number]
    'status.value': QuoteStatus[]
    'owner.id': string[]
    int_ext: string[]
  },
  invoice: boolean,
  isContactOrCompany?: boolean
) {
  const luceneFilters: ValueFilter[] = [
    filters[FILTER_STATUS_VALUE]?.length > 0 && {
      id: FILTER_STATUS_VALUE,
      type: 'equal',
      value: filters[FILTER_STATUS_VALUE]
    },
    filters.int_ext?.length > 0 && {
      id: 'int_ext',
      type: 'equal',
      value: filters.int_ext
    },
    filters?.quote_date && {
      id: 'quote_date',
      type: 'range',
      value: filters.quote_date
    },
    filters?.[FILTER_TOTALS_AFTER_TAX] && {
      id: FILTER_TOTALS_AFTER_TAX,
      type: 'range',
      value: filters[FILTER_TOTALS_AFTER_TAX]
    },
    filters['owner.id']?.length > 0 && {
      id: 'owner.id',
      type: 'equal',
      value: filters['owner.id'].map((id) => removeAuth0Prefix(id))
    },
    !isContactOrCompany && {
      id: 'is_invoice',
      type: 'equal',
      value: !!invoice
    },
    filters.archived === 'only_archived' && {
      id: 'archived',
      type: 'equal',
      value: ['true']
    }
  ]

  return luceneFilters.filter(Boolean)
}

export default QuoteSearchBar
